import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AvailabilityRequestRow,
  CreateAvailabilityRequestPayload,
  CreateAvailabilityRequestResult,
  CreateOrderPayload,
  CreateOrderResponse,
  OffersResponse,
  Order,
  OrderMessage,
  OrderMessageAttachment,
  PartsFees,
  PartsQuote,
  PaymentStatus,
  RequestOffersResponse,
} from '../models/orders/order.types';

@Injectable({ providedIn: 'root' })
export class RepuestosOrdersService {
  private readonly baseUrl = environment.apiUrl; // e.g. http://localhost:3000/

  constructor(private http: HttpClient) {}

  /** Aliados que ofrecen un producto (con providerProductId/precio/stock/ETA). */
  getOffers(productId: string): Observable<OffersResponse> {
    return this.http.get<OffersResponse>(
      `${this.baseUrl}catalog/storefront/products/${productId}/offers`,
    );
  }

  /** Crea la orden y devuelve { order, paymentUrl }. Auth corporativa (cookie). */
  createOrder(payload: CreateOrderPayload): Observable<CreateOrderResponse> {
    return this.http.post<CreateOrderResponse>(`${this.baseUrl}orders`, payload, {
      withCredentials: true,
    });
  }

  getOrder(id: string): Observable<Order> {
    return this.http.get<Order>(`${this.baseUrl}orders/${id}`, {
      withCredentials: true,
    });
  }

  /** Estado de pago de una orden (para confirmar tras volver de Stripe). */
  getPaymentStatus(orderId: string): Observable<PaymentStatus> {
    return this.http.get<PaymentStatus>(`${this.baseUrl}orders/${orderId}/payment-status`, {
      withCredentials: true,
    });
  }

  listOrders(): Observable<Order[]> {
    return this.http.get<Order[]>(`${this.baseUrl}orders`, { withCredentials: true });
  }

  getMessages(orderId: string): Observable<OrderMessage[]> {
    return this.http.get<OrderMessage[]>(`${this.baseUrl}orders/${orderId}/messages`, {
      withCredentials: true,
    });
  }

  sendMessage(
    orderId: string,
    body: { text?: string; attachments?: OrderMessageAttachment[] },
  ): Observable<OrderMessage> {
    return this.http.post<OrderMessage>(
      `${this.baseUrl}orders/${orderId}/messages`,
      body,
      { withCredentials: true },
    );
  }

  /** Sube una imagen del chat del pedido; devuelve la URL para attachments[]. */
  uploadChatPhoto(orderId: string, file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    return this.http.post<{ url: string }>(
      `${this.baseUrl}orders/${orderId}/messages/photo`,
      formData,
      { withCredentials: true },
    );
  }

  /** Solicitudes de disponibilidad del corporativo (tabla "Solicitudes"). */
  getAvailabilityRequests(): Observable<AvailabilityRequestRow[]> {
    return this.http.get<AvailabilityRequestRow[]>(
      `${this.baseUrl}juana/availability-requests`,
      { withCredentials: true },
    );
  }

  /** Ofertas de una solicitud (detalle persistente, fuera del chat). */
  getAvailabilityRequestOffers(requestId: string): Observable<RequestOffersResponse> {
    return this.http.get<RequestOffersResponse>(
      `${this.baseUrl}juana/availability-requests/${requestId}/offers`,
      { withCredentials: true },
    );
  }

  /** Crea una solicitud de disponibilidad a la red de aliados. */
  createAvailabilityRequest(
    payload: CreateAvailabilityRequestPayload,
  ): Observable<CreateAvailabilityRequestResult> {
    return this.http.post<CreateAvailabilityRequestResult>(
      `${this.baseUrl}juana/availability-requests`,
      payload,
      { withCredentials: true },
    );
  }

  /** Sube una foto de la pieza; devuelve la URL pública para attachments[]. */
  uploadAvailabilityPhoto(file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    return this.http.post<{ url: string }>(
      `${this.baseUrl}juana/availability-requests/photo`,
      formData,
      { withCredentials: true },
    );
  }

  /** Porcentajes de comisión de repuestos. */
  getPartsFees(): Observable<PartsFees> {
    return this.http.get<PartsFees>(`${this.baseUrl}fees/parts`);
  }

  /** Desglose de comisión para un subtotal/envío/instalación. */
  quoteParts(body: { subtotal: number; deliveryFee?: number; installFee?: number }): Observable<PartsQuote> {
    return this.http.post<PartsQuote>(`${this.baseUrl}fees/parts/quote`, body);
  }

  /** Cancela una orden (revierte comisión si ya se acreditó). */
  cancelOrder(orderId: string, reason?: string): Observable<Order> {
    return this.http.post<Order>(
      `${this.baseUrl}orders/${orderId}/cancel`,
      { reason },
      { withCredentials: true },
    );
  }

  /** Actualiza el link de rastreo del transportista (lo llama el aliado desde su app). */
  updateTracking(orderId: string, trackingUrl: string, carrierName?: string): Observable<Order> {
    return this.http.patch<Order>(
      `${this.baseUrl}provider/orders/${orderId}/tracking`,
      { trackingUrl, carrierName },
      { withCredentials: true },
    );
  }

  /** Corporativo confirma que recibió el pedido → acredita saldo al aliado. */
  confirmReceipt(orderId: string): Observable<Order> {
    return this.http.post<Order>(
      `${this.baseUrl}orders/${orderId}/confirm-receipt`,
      {},
      { withCredentials: true },
    );
  }
}
